import React from "react";
import { Link } from "react-router-dom";
import type { BlogArticle } from "@/types/blog";
import { getArticlePath, getPrimaryCategory } from "@/apis/blog-service";
import { formatDisplayDate } from "@/utility";
import { useLanguageContext } from "@/hooks";
import { MediaRenderer } from "@/_component/Common";
import { MediaType } from "@/types";

type ArticleCardProps = {
  article: BlogArticle;
  compact?: boolean;
  mediaHeight?: number;
};

export const ArticleCard: React.FC<ArticleCardProps> = ({ article, compact = false, mediaHeight = 260 }) => {
  const { language } = useLanguageContext();
  const readMoreLabel = language("label.read_more") || "Read more";
  const articlePath = getArticlePath(article);
  const primaryCategory = getPrimaryCategory(article);

  return (
    <article className="card h-100 border-0 shadow-sm overflow-hidden">
      <Link to={articlePath} className="bg-light">
        <MediaRenderer
          media={{ id: 0, url: article.thumbnailUrl, mediaType: MediaType.IMAGE, altText: article.title }}
          height={mediaHeight}
          objectFit="cover" />
      </Link>
      <div className="card-body d-flex flex-column gap-2">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between small text-muted">
          <Link to={`/blog/${primaryCategory.categorySlug}`} className="kg-green text-decoration-none">
            {primaryCategory.categoryName}
          </Link>
          {article.date && (
            <span>
              <i className="bi bi-calendar3 me-1" aria-hidden="true"></i>
              {formatDisplayDate(article.date)}
            </span>
          )}
        </div>
        <h3 className={compact ? "h6 text-word-break mb-0" : "h5 text-word-break mb-0"}>
          <Link to={articlePath} className="text-dark text-decoration-none">
            {article.title}
          </Link>
        </h3>
        <p className="text-muted text-multiline-truncate-3">{article.summary}</p>
        <Link to={articlePath} className="kg-green text-decoration-none mt-auto fw-semibold">
          {readMoreLabel} <i className="bi bi-arrow-right ms-1" aria-hidden="true"></i>
        </Link>
      </div>
    </article>
  );
};

export default ArticleCard;
